import type { Job, JobWithDetails, JobFilters } from '../../jobs/types/job'
import { matchesQuoteFilter } from '../../quotes/lib/quoteFilters'
import type { OrganisationWithRole } from '../../organisations/services/organisationService'
import type { InspectionNode, InspectionNodeWithDetails } from '../../jobs/types/inspectionNode'
import type { Photo, PhotoWithDetails } from '../../photos/types/photo'
import type { SyncEntity, SyncQueueItem } from './syncQueue'

const DB_NAME = 'arborscene_offline'
// v2: TEAMS store (002 R-006 — offline team selection)
// v3: ID_ALIASES store (007 — temp→server id mapping survives the remap
//     deleting the temp record, so late references still resolve)
const DB_VERSION = 3

// Pre-REL-09 database that used to hold the sync queue; still owned by the
// service worker for its own state, so it is migrated from, never deleted
const LEGACY_DB_NAME = 'arborlist-offline'

const STORES = {
  JOBS: 'jobs',
  NODES: 'nodes',
  PHOTOS: 'photos',
  SYNC_QUEUE: 'sync_queue',
  PHOTO_BLOBS: 'photo_blobs',
  TEAMS: 'teams',
  ID_ALIASES: 'id_aliases',
} as const

type StoreName = (typeof STORES)[keyof typeof STORES]

interface PhotoBlobRecord {
  photo_id: string
  blob: Blob
}

/**
 * Durable record that a temp_ entity was re-keyed to a server id. The remap
 * deletes the temp record itself, but stale references (an open page's Redux
 * state, a dead-lettered queue item) can surface the temp id much later —
 * this is how they still resolve.
 */
export interface IdAlias {
  temp_id: string
  server_id: string
  entity: SyncEntity
}

const asError = (error: DOMException | null, fallback: string): Error =>
  error ?? new Error(fallback)

export class OfflineStorageService {
  private db: IDBDatabase | null = null

  /**
   * One object URL per locally stored photo, reused across reads. Blobs are
   * write-once per photo id, so the URL never goes stale; without this cache
   * every list read minted (and leaked) a fresh blob-pinning URL.
   */
  private blobUrlCache = new Map<string, string>()

  private getOrCreateBlobUrl(photoId: string, blob: Blob): string {
    const cached = this.blobUrlCache.get(photoId)
    if (cached) return cached
    const url = URL.createObjectURL(blob)
    this.blobUrlCache.set(photoId, url)
    return url
  }

  private revokeBlobUrl(photoId: string): void {
    const url = this.blobUrlCache.get(photoId)
    if (url) {
      URL.revokeObjectURL(url)
      this.blobUrlCache.delete(photoId)
    }
  }

  /**
   * Initialize IndexedDB
   */
  async init(): Promise<void> {
    await this.openDB()
    await this.migrateLegacySyncQueue()
  }

  private async openDB(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION)

      request.onerror = () => {
        reject(new Error('Failed to open IndexedDB'))
      }

      request.onsuccess = () => {
        this.db = request.result
        resolve()
      }

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result

        // Create object stores
        if (!db.objectStoreNames.contains(STORES.JOBS)) {
          const jobStore = db.createObjectStore(STORES.JOBS, { keyPath: 'id' })
          jobStore.createIndex('status', 'status', { unique: false })
          jobStore.createIndex('assigned_arborist_id', 'assigned_arborist_id', { unique: false })
          jobStore.createIndex('client_org_id', 'client_org_id', { unique: false })
          jobStore.createIndex('created_at', 'created_at', { unique: false })
        }

        if (!db.objectStoreNames.contains(STORES.NODES)) {
          const nodeStore = db.createObjectStore(STORES.NODES, { keyPath: 'id' })
          nodeStore.createIndex('job_id', 'job_id', { unique: false })
          nodeStore.createIndex('node_number', 'node_number', { unique: false })
        }

        if (!db.objectStoreNames.contains(STORES.PHOTOS)) {
          const photoStore = db.createObjectStore(STORES.PHOTOS, { keyPath: 'id' })
          photoStore.createIndex('job_id', 'job_id', { unique: false })
          photoStore.createIndex('node_id', 'node_id', { unique: false })
        }

        if (!db.objectStoreNames.contains(STORES.PHOTO_BLOBS)) {
          db.createObjectStore(STORES.PHOTO_BLOBS, { keyPath: 'photo_id' })
        }

        if (!db.objectStoreNames.contains(STORES.SYNC_QUEUE)) {
          const syncStore = db.createObjectStore(STORES.SYNC_QUEUE, { keyPath: 'id' })
          syncStore.createIndex('created_at', 'created_at', { unique: false })
          syncStore.createIndex('type', 'type', { unique: false })
        }

        if (!db.objectStoreNames.contains(STORES.TEAMS)) {
          db.createObjectStore(STORES.TEAMS, { keyPath: 'id' })
        }

        if (!db.objectStoreNames.contains(STORES.ID_ALIASES)) {
          db.createObjectStore(STORES.ID_ALIASES, { keyPath: 'temp_id' })
        }
      }
    })
  }

  /**
   * Ensure DB is initialized
   */
  private async ensureDB(): Promise<IDBDatabase> {
    if (!this.db) {
      await this.openDB()
    }
    if (!this.db) {
      throw new Error('IndexedDB unavailable')
    }
    return this.db
  }

  /**
   * Run one read request against a store and resolve with its typed result
   */
  private async read<T>(
    storeName: StoreName,
    operate: (store: IDBObjectStore) => IDBRequest
  ): Promise<T> {
    const db = await this.ensureDB()
    return new Promise((resolve, reject) => {
      const request = operate(db.transaction(storeName, 'readonly').objectStore(storeName))
      request.onsuccess = () => resolve(request.result as T)
      request.onerror = () =>
        reject(asError(request.error, `IndexedDB read from ${storeName} failed`))
    })
  }

  /**
   * Run one readwrite transaction across multiple stores. Resolves only when
   * the transaction commits, so the writes are all-or-nothing (REL-09).
   */
  private async runWrite(
    storeNames: StoreName[],
    apply: (tx: IDBTransaction) => void
  ): Promise<void> {
    const db = await this.ensureDB()
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(storeNames, 'readwrite')
      transaction.oncomplete = () => resolve()
      transaction.onerror = () => reject(asError(transaction.error, 'IndexedDB write failed'))
      transaction.onabort = () =>
        reject(asError(transaction.error, 'IndexedDB transaction aborted'))
      apply(transaction)
    })
  }

  /**
   * One-time import of queued items from the pre-REL-09 database, so nothing
   * captured before this release is stranded.
   */
  private async migrateLegacySyncQueue(): Promise<void> {
    try {
      // Skip when we can prove the legacy DB doesn't exist (avoids creating it)
      if (indexedDB.databases) {
        const databases = await indexedDB.databases()
        if (!databases.some((info) => info.name === LEGACY_DB_NAME)) return
      }

      const legacy = await new Promise<IDBDatabase>((resolve, reject) => {
        const request = indexedDB.open(LEGACY_DB_NAME)
        request.onsuccess = () => resolve(request.result)
        request.onerror = () => reject(asError(request.error, 'Failed to open legacy IndexedDB'))
      })

      if (!legacy.objectStoreNames.contains('sync_queue')) {
        legacy.close()
        return
      }

      const items = await new Promise<SyncQueueItem[]>((resolve, reject) => {
        const request = legacy
          .transaction('sync_queue', 'readonly')
          .objectStore('sync_queue')
          .getAll()
        request.onsuccess = () => resolve((request.result ?? []) as SyncQueueItem[])
        request.onerror = () => reject(asError(request.error, 'Failed to read legacy sync queue'))
      })

      if (items.length > 0) {
        await this.runWrite([STORES.SYNC_QUEUE], (tx) => {
          const store = tx.objectStore(STORES.SYNC_QUEUE)
          items.forEach((item) => store.put(item))
        })
        await new Promise<void>((resolve, reject) => {
          const tx = legacy.transaction('sync_queue', 'readwrite')
          tx.objectStore('sync_queue').clear()
          tx.oncomplete = () => resolve()
          tx.onerror = () => reject(asError(tx.error, 'Failed to clear legacy sync queue'))
        })
      }

      legacy.close()
    } catch (error) {
      console.warn('Legacy sync-queue migration skipped:', error)
    }
  }

  // ==================== SYNC QUEUE OPERATIONS ====================

  async getQueueItems(): Promise<SyncQueueItem[]> {
    const items = await this.read<SyncQueueItem[] | undefined>(STORES.SYNC_QUEUE, (store) =>
      store.getAll()
    )
    return items ?? []
  }

  async getQueueItem(id: string): Promise<SyncQueueItem | null> {
    const item = await this.read<SyncQueueItem | undefined>(STORES.SYNC_QUEUE, (store) =>
      store.get(id)
    )
    return item ?? null
  }

  async putQueueItem(item: SyncQueueItem): Promise<void> {
    return this.runWrite([STORES.SYNC_QUEUE], (tx) => {
      tx.objectStore(STORES.SYNC_QUEUE).put(item)
    })
  }

  async deleteQueueItem(id: string): Promise<void> {
    return this.runWrite([STORES.SYNC_QUEUE], (tx) => {
      tx.objectStore(STORES.SYNC_QUEUE).delete(id)
    })
  }

  // ============ ATOMIC ENTITY + QUEUE WRITES (REL-09) ============
  // An offline mutation and its sync-queue entry commit together: either the
  // change is saved AND queued for upload, or neither happens.

  async saveJobWithQueueItem(job: Job, queueItem: SyncQueueItem): Promise<void> {
    return this.runWrite([STORES.JOBS, STORES.SYNC_QUEUE], (tx) => {
      tx.objectStore(STORES.JOBS).put(job)
      tx.objectStore(STORES.SYNC_QUEUE).put(queueItem)
    })
  }

  async deleteJobWithQueueItem(id: string, queueItem: SyncQueueItem): Promise<void> {
    return this.runWrite([STORES.JOBS, STORES.SYNC_QUEUE], (tx) => {
      tx.objectStore(STORES.JOBS).delete(id)
      tx.objectStore(STORES.SYNC_QUEUE).put(queueItem)
    })
  }

  async saveNodeWithQueueItem(node: InspectionNode, queueItem: SyncQueueItem): Promise<void> {
    return this.runWrite([STORES.NODES, STORES.SYNC_QUEUE], (tx) => {
      tx.objectStore(STORES.NODES).put(node)
      tx.objectStore(STORES.SYNC_QUEUE).put(queueItem)
    })
  }

  async deleteNodeWithQueueItem(id: string, queueItem: SyncQueueItem): Promise<void> {
    return this.runWrite([STORES.NODES, STORES.SYNC_QUEUE], (tx) => {
      tx.objectStore(STORES.NODES).delete(id)
      tx.objectStore(STORES.SYNC_QUEUE).put(queueItem)
    })
  }

  async savePhotoWithQueueItem(
    photo: Photo,
    queueItem: SyncQueueItem,
    blob?: File | Blob
  ): Promise<void> {
    return this.runWrite([STORES.PHOTOS, STORES.PHOTO_BLOBS, STORES.SYNC_QUEUE], (tx) => {
      tx.objectStore(STORES.PHOTOS).put(photo)
      if (blob) {
        tx.objectStore(STORES.PHOTO_BLOBS).put({ photo_id: photo.id, blob })
      }
      tx.objectStore(STORES.SYNC_QUEUE).put(queueItem)
    })
  }

  async deletePhotoWithQueueItem(id: string, queueItem: SyncQueueItem): Promise<void> {
    this.revokeBlobUrl(id)
    return this.runWrite([STORES.PHOTOS, STORES.PHOTO_BLOBS, STORES.SYNC_QUEUE], (tx) => {
      tx.objectStore(STORES.PHOTOS).delete(id)
      tx.objectStore(STORES.PHOTO_BLOBS).delete(id)
      tx.objectStore(STORES.SYNC_QUEUE).put(queueItem)
    })
  }

  /**
   * Swap a temp_ node id for the server-assigned row after its create syncs:
   * replaces the node record, repoints local photos, and rewrites queue items
   * (including dead-lettered ones, so manual retry works) that still
   * reference the temp id — all in one transaction (REL-09).
   */
  async remapNodeId(tempNodeId: string, serverNode: InspectionNode): Promise<void> {
    return this.runWrite(
      [STORES.NODES, STORES.PHOTOS, STORES.SYNC_QUEUE, STORES.ID_ALIASES],
      (tx) => {
        const nodes = tx.objectStore(STORES.NODES)
        nodes.delete(tempNodeId)
        nodes.put(serverNode)

        tx.objectStore(STORES.ID_ALIASES).put({
          temp_id: tempNodeId,
          server_id: serverNode.id,
          entity: 'node',
        } satisfies IdAlias)

        const photoCursor = tx
          .objectStore(STORES.PHOTOS)
          .index('node_id')
          .openCursor(IDBKeyRange.only(tempNodeId))
        photoCursor.onsuccess = () => {
          const cursor = photoCursor.result
          if (!cursor) return
          cursor.update({ ...(cursor.value as Photo), node_id: serverNode.id })
          cursor.continue()
        }

        const queueCursor = tx.objectStore(STORES.SYNC_QUEUE).openCursor()
        queueCursor.onsuccess = () => {
          const cursor = queueCursor.result
          if (!cursor) return
          const item = cursor.value as SyncQueueItem
          let changed = false
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          if (item.entity === 'photo' && item.data?.node_id === tempNodeId) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            item.data = { ...item.data, node_id: serverNode.id }
            changed = true
          }
          if (item.entity === 'node' && item.entityId === tempNodeId) {
            item.entityId = serverNode.id
            changed = true
          }
          if (changed) cursor.update(item)
          cursor.continue()
        }
      }
    )
  }

  /**
   * Swap a temp_ photo id for the server-assigned row after its create
   * syncs: re-keys the local record and its blob, and rewrites queued items
   * (offline caption edits, deletes) still keyed to the temp id so they
   * replay against the real row — all in one transaction (REL-09).
   */
  async remapPhotoId(tempPhotoId: string, serverPhoto: Photo): Promise<void> {
    this.revokeBlobUrl(tempPhotoId)
    return this.runWrite(
      [STORES.PHOTOS, STORES.PHOTO_BLOBS, STORES.SYNC_QUEUE, STORES.ID_ALIASES],
      (tx) => {
        const photos = tx.objectStore(STORES.PHOTOS)
        photos.delete(tempPhotoId)
        photos.put(serverPhoto)

        tx.objectStore(STORES.ID_ALIASES).put({
          temp_id: tempPhotoId,
          server_id: serverPhoto.id,
          entity: 'photo',
        } satisfies IdAlias)

        const blobs = tx.objectStore(STORES.PHOTO_BLOBS)
        const blobReq = blobs.get(tempPhotoId)
        blobReq.onsuccess = () => {
          const record = blobReq.result as PhotoBlobRecord | undefined
          if (!record) return
          blobs.put({ photo_id: serverPhoto.id, blob: record.blob })
          blobs.delete(tempPhotoId)
        }

        const queueCursor = tx.objectStore(STORES.SYNC_QUEUE).openCursor()
        queueCursor.onsuccess = () => {
          const cursor = queueCursor.result
          if (!cursor) return
          const item = cursor.value as SyncQueueItem
          if (item.entity === 'photo' && item.entityId === tempPhotoId) {
            item.entityId = serverPhoto.id
            const data = item.data as { id?: unknown } | undefined
            if (data?.id === tempPhotoId) {
              item.data = { ...data, id: serverPhoto.id }
            }
            cursor.update(item)
          }
          cursor.continue()
        }
      }
    )
  }

  /**
   * Swap a temp_ job id for the server-assigned row after its create syncs:
   * replaces the job record, repoints local nodes/photos, and rewrites queue
   * items still referencing the temp id — including the temp-prefixed
   * storage keys queued photos would otherwise upload under (REL-09).
   */
  async remapJobId(tempJobId: string, serverJob: Job): Promise<void> {
    const rewriteStoragePath = (path: string): string =>
      path.startsWith(`${tempJobId}/`)
        ? `${serverJob.id}/${path.slice(tempJobId.length + 1)}`
        : path

    return this.runWrite(
      [STORES.JOBS, STORES.NODES, STORES.PHOTOS, STORES.SYNC_QUEUE, STORES.ID_ALIASES],
      (tx) => {
        const jobs = tx.objectStore(STORES.JOBS)
        jobs.delete(tempJobId)
        jobs.put(serverJob)

        tx.objectStore(STORES.ID_ALIASES).put({
          temp_id: tempJobId,
          server_id: serverJob.id,
          entity: 'job',
        } satisfies IdAlias)

        const nodeCursor = tx
          .objectStore(STORES.NODES)
          .index('job_id')
          .openCursor(IDBKeyRange.only(tempJobId))
        nodeCursor.onsuccess = () => {
          const cursor = nodeCursor.result
          if (!cursor) return
          cursor.update({ ...(cursor.value as InspectionNode), job_id: serverJob.id })
          cursor.continue()
        }

        const photoCursor = tx
          .objectStore(STORES.PHOTOS)
          .index('job_id')
          .openCursor(IDBKeyRange.only(tempJobId))
        photoCursor.onsuccess = () => {
          const cursor = photoCursor.result
          if (!cursor) return
          const photo = cursor.value as Photo
          cursor.update({
            ...photo,
            job_id: serverJob.id,
            storage_path: rewriteStoragePath(photo.storage_path),
          })
          cursor.continue()
        }

        const queueCursor = tx.objectStore(STORES.SYNC_QUEUE).openCursor()
        queueCursor.onsuccess = () => {
          const cursor = queueCursor.result
          if (!cursor) return
          const item = cursor.value as SyncQueueItem
          let changed = false
          if (item.entity === 'job' && item.entityId === tempJobId) {
            item.entityId = serverJob.id
            changed = true
          }
          const data = item.data as { job_id?: unknown; storage_path?: unknown } | undefined
          if (data?.job_id === tempJobId) {
            item.data = {
              ...data,
              job_id: serverJob.id,
              ...(item.entity === 'photo' && typeof data.storage_path === 'string'
                ? { storage_path: rewriteStoragePath(data.storage_path) }
                : {}),
            }
            changed = true
          }
          if (changed) cursor.update(item)
          cursor.continue()
        }
      }
    )
  }

  /** Look up the server id a temp_ id was remapped to, if any */
  async getIdAlias(tempId: string): Promise<IdAlias | null> {
    const alias = await this.read<IdAlias | undefined>(STORES.ID_ALIASES, (store) =>
      store.get(tempId)
    )
    return alias ?? null
  }

  /**
   * Resolve an id that may be a stale temp_ reference (held by an open page
   * across a background remap) to its server id; non-temp ids pass through.
   */
  async resolveId(id: string): Promise<string> {
    if (!id.startsWith('temp_')) return id
    const alias = await this.getIdAlias(id)
    return alias?.server_id ?? id
  }

  /**
   * Best-effort recovery for photos stranded on a temp_ node id whose node
   * already synced before remapping existed (pre-007 builds discarded the
   * server id at sync time). The temp record lingers in the local store, so
   * match it to the one synced node in the same job at the same coordinates;
   * any ambiguity returns null and the photo keeps waiting.
   */
  async findSyncedNodeMatch(tempNodeId: string): Promise<InspectionNodeWithDetails | null> {
    const tempNode = await this.getNode(tempNodeId)
    if (!tempNode) return null

    const candidates = (await this.getNodesByJobId(tempNode.job_id)).filter(
      (node) =>
        !node.id.startsWith('temp_') &&
        node.gps_latitude === tempNode.gps_latitude &&
        node.gps_longitude === tempNode.gps_longitude
    )
    return candidates.length === 1 ? (candidates[0] ?? null) : null
  }

  // ==================== TEAM OPERATIONS ====================

  /**
   * Replace the cached team list (002 R-006). Written through on every
   * successful team fetch; replaced wholesale so a team the user has left
   * cannot linger as a selectable option offline.
   */
  async saveTeams(teams: OrganisationWithRole[]): Promise<void> {
    return this.runWrite([STORES.TEAMS], (tx) => {
      const store = tx.objectStore(STORES.TEAMS)
      store.clear()
      teams.forEach((team) => store.put(team))
    })
  }

  async getTeams(): Promise<OrganisationWithRole[]> {
    const teams = await this.read<OrganisationWithRole[] | undefined>(STORES.TEAMS, (store) =>
      store.getAll()
    )
    return teams ?? []
  }

  // ==================== JOB OPERATIONS ====================

  async saveJob(job: Job): Promise<void> {
    return this.runWrite([STORES.JOBS], (tx) => {
      tx.objectStore(STORES.JOBS).put(job)
    })
  }

  async getJob(id: string): Promise<JobWithDetails | null> {
    const job = await this.read<Job | undefined>(STORES.JOBS, (store) => store.get(id))
    if (!job) return null
    return {
      ...job,
      sync_status: 'pending' as const,
      stored_locally: true,
    }
  }

  async getJobs(filters?: JobFilters): Promise<JobWithDetails[]> {
    let jobs = (await this.read<Job[] | undefined>(STORES.JOBS, (store) => store.getAll())) ?? []

    // Mirror the server-side filters in jobService.getJobs
    if (filters?.status) {
      const statuses = Array.isArray(filters.status) ? filters.status : [filters.status]
      jobs = jobs.filter((job) => (statuses as string[]).includes(job.status))
    }

    if (filters?.assigned_arborist_id) {
      jobs = jobs.filter((job) => job.arborist_id === filters.assigned_arborist_id)
    }

    if (filters?.show_unassigned) {
      jobs = jobs.filter((job) => job.arborist_id === null)
    }

    if (filters?.org_id) {
      jobs = jobs.filter((job) => job.org_id === filters.org_id)
    }

    if (filters?.client_org_id) {
      jobs = jobs.filter((job) => job.client_org_id === filters.client_org_id)
    }

    if (filters?.search_query) {
      const query = filters.search_query.toLowerCase()
      jobs = jobs.filter(
        (job) =>
          (job.title ?? '').toLowerCase().includes(query) ||
          (job.site_address_line1 ?? '').toLowerCase().includes(query)
      )
    }

    if (filters?.date_from) {
      const dateFrom = filters.date_from
      jobs = jobs.filter((job) => (job.created_at ?? '') >= dateFrom)
    }

    if (filters?.quote_status) {
      const quoteFilter = filters.quote_status
      jobs = jobs.filter((job) =>
        matchesQuoteFilter((job as JobWithDetails).current_quote, quoteFilter)
      )
    }

    if (filters?.date_to) {
      const dateTo = filters.date_to
      jobs = jobs.filter((job) => (job.created_at ?? '') <= dateTo)
    }

    return jobs.map((job) => ({
      ...job,
      sync_status: 'pending' as const,
      stored_locally: true,
    }))
  }

  async deleteJob(id: string): Promise<void> {
    return this.runWrite([STORES.JOBS], (tx) => {
      tx.objectStore(STORES.JOBS).delete(id)
    })
  }

  /**
   * Cache multiple jobs from server (for background sync)
   */
  async cacheJobs(jobs: Job[]): Promise<void> {
    return this.runWrite([STORES.JOBS], (tx) => {
      const store = tx.objectStore(STORES.JOBS)
      jobs.forEach((job) => store.put(job))
    })
  }

  // ==================== NODE OPERATIONS ====================

  async saveNode(node: InspectionNode): Promise<void> {
    return this.runWrite([STORES.NODES], (tx) => {
      tx.objectStore(STORES.NODES).put(node)
    })
  }

  async getNode(id: string): Promise<InspectionNodeWithDetails | null> {
    const node = await this.read<InspectionNode | undefined>(STORES.NODES, (store) => store.get(id))
    if (!node) return null
    return {
      ...node,
      sync_status: 'pending' as const,
      stored_locally: true,
    }
  }

  async getNodesByJobId(jobId: string): Promise<InspectionNodeWithDetails[]> {
    const nodes =
      (await this.read<InspectionNode[] | undefined>(STORES.NODES, (store) =>
        store.index('job_id').getAll(jobId)
      )) ?? []

    return nodes
      .map((node) => ({
        ...node,
        sync_status: 'pending' as const,
        stored_locally: true,
      }))
      .sort((a, b) => (a.node_number || 0) - (b.node_number || 0))
  }

  async deleteNode(id: string): Promise<void> {
    return this.runWrite([STORES.NODES], (tx) => {
      tx.objectStore(STORES.NODES).delete(id)
    })
  }

  // ==================== PHOTO OPERATIONS ====================

  async savePhoto(photo: Photo, blob?: File | Blob): Promise<void> {
    // Replacing the stored bytes invalidates any URL minted for the old blob
    if (blob) this.revokeBlobUrl(photo.id)
    return this.runWrite([STORES.PHOTOS, STORES.PHOTO_BLOBS], (tx) => {
      tx.objectStore(STORES.PHOTOS).put(photo)
      if (blob) {
        tx.objectStore(STORES.PHOTO_BLOBS).put({ photo_id: photo.id, blob })
      }
    })
  }

  /**
   * Raw local blob for a photo, if it was captured offline and is still
   * awaiting sync. Used by PDF generation to avoid a network round-trip.
   */
  async getPhotoBlob(id: string): Promise<Blob | null> {
    const blobRecord = await this.read<PhotoBlobRecord | undefined>(STORES.PHOTO_BLOBS, (store) =>
      store.get(id)
    )
    return blobRecord?.blob ?? null
  }

  async getPhoto(id: string): Promise<PhotoWithDetails | null> {
    const photo = await this.read<Photo | undefined>(STORES.PHOTOS, (store) => store.get(id))
    if (!photo) return null

    const blobRecord = await this.read<PhotoBlobRecord | undefined>(STORES.PHOTO_BLOBS, (store) =>
      store.get(id)
    )
    const local_blob_url = blobRecord?.blob
      ? this.getOrCreateBlobUrl(id, blobRecord.blob)
      : undefined

    return {
      ...photo,
      sync_status: 'pending' as const,
      stored_locally: true,
      local_blob_url,
    }
  }

  /**
   * Decorate photo rows with an object URL for their locally stored bytes
   * (offline captures awaiting sync, and cached synced copies), so they can
   * render without the network. Callers that only need the rows (photo
   * counts) pass withBlobUrls=false and skip the blob reads entirely.
   */
  private async attachLocalBlobUrls(
    photos: Photo[],
    withBlobUrls: boolean
  ): Promise<PhotoWithDetails[]> {
    return Promise.all(
      photos.map(async (photo) => {
        const blobRecord = withBlobUrls
          ? await this.read<PhotoBlobRecord | undefined>(STORES.PHOTO_BLOBS, (store) =>
              store.get(photo.id)
            )
          : undefined
        return {
          ...photo,
          sync_status: 'pending' as const,
          stored_locally: true,
          local_blob_url: blobRecord?.blob
            ? this.getOrCreateBlobUrl(photo.id, blobRecord.blob)
            : undefined,
        }
      })
    )
  }

  async getPhotosByJobId(jobId: string, withBlobUrls = true): Promise<PhotoWithDetails[]> {
    const photos =
      (await this.read<Photo[] | undefined>(STORES.PHOTOS, (store) =>
        store.index('job_id').getAll(jobId)
      )) ?? []

    return this.attachLocalBlobUrls(photos, withBlobUrls)
  }

  async getPhotosByNodeId(nodeId: string, withBlobUrls = true): Promise<PhotoWithDetails[]> {
    const photos =
      (await this.read<Photo[] | undefined>(STORES.PHOTOS, (store) =>
        store.index('node_id').getAll(nodeId)
      )) ?? []

    return this.attachLocalBlobUrls(photos, withBlobUrls)
  }

  async deletePhoto(id: string): Promise<void> {
    this.revokeBlobUrl(id)
    return this.runWrite([STORES.PHOTOS, STORES.PHOTO_BLOBS], (tx) => {
      tx.objectStore(STORES.PHOTOS).delete(id)
      tx.objectStore(STORES.PHOTO_BLOBS).delete(id)
    })
  }

  /**
   * Ids of photos whose create has not completed — they exist locally but
   * the server cannot know them yet (pending, in-flight, or dead-lettered).
   * Used by the list merges to tell "awaiting sync" apart from "deleted on
   * another device".
   */
  async getPendingPhotoCreateIds(): Promise<Set<string>> {
    const items = await this.getQueueItems()
    return new Set(
      items
        .filter((item) => item.entity === 'photo' && item.operation === 'create')
        .map((item) => item.entityId)
    )
  }

  /**
   * Clear all offline data
   */
  async clearAll(): Promise<void> {
    for (const id of [...this.blobUrlCache.keys()]) this.revokeBlobUrl(id)
    return this.runWrite(
      [
        STORES.JOBS,
        STORES.NODES,
        STORES.PHOTOS,
        STORES.PHOTO_BLOBS,
        STORES.SYNC_QUEUE,
        STORES.ID_ALIASES,
      ],
      (tx) => {
        tx.objectStore(STORES.JOBS).clear()
        tx.objectStore(STORES.NODES).clear()
        tx.objectStore(STORES.PHOTOS).clear()
        tx.objectStore(STORES.PHOTO_BLOBS).clear()
        tx.objectStore(STORES.SYNC_QUEUE).clear()
        tx.objectStore(STORES.ID_ALIASES).clear()
      }
    )
  }
}

// Export singleton instance
export const offlineStorageService = new OfflineStorageService()

// Initialize on module load
offlineStorageService.init().catch((error: unknown) => {
  console.error('Failed to initialize offline storage:', error)
})
